import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CALL_OUT_OF_JURISDICTION } from '@rsos/constants/collaborator';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import { useGetConversationID } from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useGetSelectedCollaborator } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import useIsFordCall from '../../../../hooks/useIsFordCall';
import { useChatComponents } from '../../useChatComponents';
import { FullWidthContent } from '../tabs/OverviewTab/OverviewTab.styles';
import ChatCardDisplay from './ChatCardDisplay';

/**
 * Chat card wrapper component that is displayed on the overview tab.
 */
const ChatCard = () => {
  const { isChatDisabled } = useChatComponents();

  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const collaborator = useGetSelectedCollaborator();
  const isOutOfJurisdiction = collaborator.type === CALL_OUT_OF_JURISDICTION;

  const hasConversation = Boolean(useGetConversationID(selectedCallerID));

  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  const isFord = useIsFordCall(selectedCallerID);
  // Standard TF911 — never for Ford VIN-keyed rows
  const hasAccessToTF911 = useIsTF911Enabled() && !isFord;

  // Check to see if it's an RCS message and that it's not timed out or declined
  const shouldRenderRcsChatCard = useMemo(() => {
    return Boolean(rcsConversation && rcsConversation.data);
  }, [rcsConversation]);

  if (isChatDisabled || isOutOfJurisdiction) {
    return null;
  }

  return (
    <FullWidthContent
      backgroundColor="background"
      data-name="overview-chat-card"
      noPadding
    >
      <ChatCardDisplay
        hasConversation={hasConversation}
        hasAccessToTF911={hasAccessToTF911}
        shouldRenderRcsChatCard={shouldRenderRcsChatCard}
      />
    </FullWidthContent>
  );
};

export default ChatCard;
